<template>
  <div class="coparticipation-table">
    <v-data-table
      id="CoparticipationTable"
      class="default-table-height elevation-1"
      item-key="id"
      :headers="headers"
      :items="records"
      :loading="loadingTable"
      :items-per-page="pagination.itemsPerPage"
      :server-items-length="pagination.totalItems"
      :page="pagination.page"
      :no-data-text="emptyTableText"
      :loading-text="$t('coparticipation.table.loading')"
      :no-result-text="$t('coparticipation.table.noResult')"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 25, 50, 100],
        disablePagination: loadingTable,
      }"
      @update:options="onUpdateOptions"
    >
      <template v-slot:[`header.carrierGroup`]>
        <div class="coparticipation-table__header-stack">
          <span>{{ $t('coparticipation.table.headers.carrier') }}</span>
          <span>{{ $t('coparticipation.table.headers.financialGroup') }}</span>
        </div>
      </template>

      <template v-slot:[`header.contractGroup`]>
        <div class="coparticipation-table__header-stack">
          <span>{{ $t('coparticipation.table.headers.contract') }}</span>
          <span>{{ $t('coparticipation.table.headers.subcontract') }}</span>
        </div>
      </template>

      <template v-slot:[`header.beneficiaryGroup`]>
        <div class="coparticipation-table__header-stack">
          <span>{{ $t('coparticipation.table.headers.beneficiary') }}</span>
          <span>{{ $t('coparticipation.table.headers.ownership') }}</span>
        </div>
      </template>

      <template v-slot:[`header.eventGroup`]>
        <div class="coparticipation-table__header-stack">
          <span>{{ $t('coparticipation.table.headers.event') }}</span>
          <span>{{ $t('coparticipation.table.headers.eventDate') }}</span>
        </div>
      </template>

      <template v-slot:[`header.copayGroup`]>
        <div class="coparticipation-table__header-stack">
          <span>{{ $t('coparticipation.table.headers.copayAmount') }}</span>
          <span>{{ $t('coparticipation.table.headers.copayExempt') }}</span>
        </div>
      </template>

      <template v-slot:[`item.carrierGroup`]="{ item }">
        <div class="coparticipation-table__stack">
          <span class="coparticipation-table__primary">{{ displayValue(item.carrier) }}</span>
          <span class="coparticipation-table__secondary">{{ displayValue(item.financialGroup) }}</span>
        </div>
      </template>

      <template v-slot:[`item.contractGroup`]="{ item }">
        <div class="coparticipation-table__stack">
          <span class="coparticipation-table__primary">{{ displayValue(item.contract) }}</span>
          <span class="coparticipation-table__secondary">{{ displayValue(item.subcontract) }}</span>
        </div>
      </template>

      <template v-slot:[`item.competence`]="{ item }">
        <span class="coparticipation-table__primary">{{ formatCompetence(item.competence) }}</span>
      </template>

      <template v-slot:[`item.beneficiaryGroup`]="{ item }">
        <div class="coparticipation-table__stack">
          <span class="coparticipation-table__primary">{{ displayValue(item.beneficiary) }}</span>
          <span class="coparticipation-table__secondary">{{ formatOwnership(item.ownership) }}</span>
        </div>
      </template>

      <template v-slot:[`item.plan`]="{ item }">
        <span class="coparticipation-table__primary">{{ displayValue(item.plan) }}</span>
      </template>

      <template v-slot:[`item.eventGroup`]="{ item }">
        <div class="coparticipation-table__stack">
          <span class="coparticipation-table__primary">{{ displayValue(item.event) }}</span>
          <span class="coparticipation-table__secondary">{{ formatEventDate(item.eventDate) }}</span>
        </div>
      </template>

      <template v-slot:[`item.provider`]="{ item }">
        <span class="coparticipation-table__primary">{{ displayValue(item.provider) }}</span>
      </template>

      <template v-slot:[`item.copayGroup`]="{ item }">
        <div class="coparticipation-table__stack">
          <span class="coparticipation-table__primary coparticipation-table__amount">
            {{ formatAmount(item.copayAmount) }}
          </span>
          <span
            v-if="item.copayExempt === true || item.copayExempt === false"
            class="coparticipation-table__exempt-badge"
            :class="item.copayExempt
              ? 'coparticipation-table__exempt-badge--yes'
              : 'coparticipation-table__exempt-badge--no'"
          >
            {{ formatExempt(item.copayExempt) }}
          </span>
          <span
            v-else
            class="coparticipation-table__secondary"
          >
            —
          </span>
        </div>
      </template>

      <template v-slot:[`item.reason`]="{ item }">
        <span class="coparticipation-table__primary">{{ displayValue(item.reason) }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import Formatters from '@/shared/formatters/formatters';

export default {
  name: 'CoparticipationTable',
  data() {
    return { formatter: new Formatters() };
  },
  computed: {
    ...mapGetters({
      records: 'coparticipation/records',
      loadingTable: 'coparticipation/loadingTable',
      pagination: 'coparticipation/pagination',
      hasSearched: 'coparticipation/hasSearched',
    }),
    emptyTableText() {
      return this.hasSearched
        ? this.$t('coparticipation.table.noData')
        : this.$t('coparticipation.messages.requiredFilters');
    },
    headers() {
      return [
        {
          text: this.$t('coparticipation.table.headers.carrier'),
          value: 'carrierGroup',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.contract'),
          value: 'contractGroup',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.competence'),
          value: 'competence',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.beneficiary'),
          value: 'beneficiaryGroup',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.plan'),
          value: 'plan',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.event'),
          value: 'eventGroup',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.provider'),
          value: 'provider',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.copayAmount'),
          value: 'copayGroup',
          sortable: false,
        },
        {
          text: this.$t('coparticipation.table.headers.reason'),
          value: 'reason',
          sortable: false,
        },
      ];
    },
  },
  methods: {
    ...mapActions({ updateTableOptions: 'coparticipation/updateTableOptions' }),
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '—';
      return value;
    },
    formatOwnership(ownership) {
      if (!ownership) return '—';
      const normalized = String(ownership).toUpperCase();
      if (normalized === 'HOLDER') {
        return this.$t('coparticipation.table.ownership.holder');
      }
      if (normalized === 'DEPENDENT') {
        return this.$t('coparticipation.table.ownership.dependent');
      }
      return ownership;
    },
    formatExempt(copayExempt) {
      if (copayExempt === true) return this.$t('coparticipation.table.yes');
      if (copayExempt === false) return this.$t('coparticipation.table.no');
      return '—';
    },
    formatCompetence(competence) {
      if (!competence) return '—';
      const parsed = moment(competence, ['YYYY-MM', 'YYYY-MM-DD', moment.ISO_8601], true);
      if (!parsed.isValid()) return competence;
      const locale = (this.$i18n && this.$i18n.locale) || 'pt-br';
      const formatted = parsed.locale(locale).format('MMM/YYYY');
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    formatEventDate(eventDate) {
      if (!eventDate) return '—';
      const parsed = moment(eventDate, ['YYYY-MM-DD', moment.ISO_8601], true);
      if (!parsed.isValid()) return eventDate;
      return parsed.format('DD/MM/YYYY');
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === '') return '—';
      const floatValue = Number(amount);
      if (Number.isNaN(floatValue)) return '—';
      return this.formatter.formatToBRLCurrency(floatValue) || '—';
    },
    async onUpdateOptions({ page, itemsPerPage }) {
      if (!page || !itemsPerPage || itemsPerPage < 0) return;
      await this.updateTableOptions({ page, itemsPerPage });
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/coparticipation/table.scss";
</style>
